import {
  ColDef,
  ColGroupDef,
  ICellRendererParams,
  ValueFormatterParams,
  ValueGetterParams,
} from '@ag-grid-community/core';
import dayjs from 'dayjs';

import {
  AgColumnGroupHeaderComponent,
  AgColumnGroupHeaderExternalParams,
} from '@shared/ag-components/ag-column-group-header/ag-column-group-header';
import { AgHeaderLinkComponent } from '@shared/ag-components/ag-header-link/ag-header-link.component';
import { AgIconLinkCellComponent } from '@shared/ag-components/ag-icon-link-cell/ag-icon-link-cell.component';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { decimalDivide } from '@shared/utils';
import { getCellClassForGroups } from '@shared/utils/ag-cell-class-for-groups';
import { Utils } from '@shared/utils/utils';

import {
  currencyFormatter,
  defaultHeaderClass,
  defaultMinWidth,
  defaultWidthForMoneyColumns,
  InMonthAdjustmentGridConfigContext,
} from './ima-grid-config-context';

export const imaInvoiceColumnGroup = (ctx: InMonthAdjustmentGridConfigContext): ColGroupDef => {
  const { periodCloseComponent, redirectFromInvoiceColumn, selected_month } = ctx.qcaComponent;
  const eyeIconTooltip = 'Review Invoices with Service Period set to this month.';

  const isOpenMonth =
    `${dayjs(selected_month.value).format('YYYY-MM')}-01` === periodCloseComponent.currentMonth;

  return {
    children: [
      getInvoicesPercentageColumn(),
      getAvailableInvoiceColumn(ctx),
      getMonthlyExpenseColumn(),
    ].filter((column) => {
      if (isOpenMonth) {
        return true;
      }

      return column.field !== 'invoices_available';
    }),
    headerClass: ['header-marker', 'blue-light-marker', 'text-center'],
    headerGroupComponent: AgColumnGroupHeaderComponent,
    headerGroupComponentParams: <AgColumnGroupHeaderExternalParams>{
      buttons: [
        {
          icon: 'Eye',
          onClick: redirectFromInvoiceColumn,
          tooltip: eyeIconTooltip,
        },
      ],
      expansionDisabled: true,
    },
    headerName: 'Invoice',
  };
};

const getInvoicesPercentageColumn = (): ColDef => ({
  cellClass: (params) => ['text-right', ...getCellClassForGroups(params)],
  colId: 'invoices_percentage',
  field: 'invoices_percentage',
  filter: 'agNumberColumnFilter',
  headerClass: defaultHeaderClass,
  headerName: 'Monthly % Complete',
  headerTooltip: 'Monthly % Complete',
  minWidth: defaultMinWidth,
  valueFormatter: ({ value }: ValueFormatterParams) =>
    Utils.percentageFormatter(decimalDivide(value, 100), { maximumFractionDigits: 4 }),
  valueGetter: (params: ValueGetterParams) => {
    const total_amount = !params.data
      ? params.node?.aggData.total_amount
      : params.data.total_amount;

    const invoices_amount = !params.data
      ? params.node?.aggData.invoices_amount
      : params.data.invoices_amount;

    return (invoices_amount / total_amount) * 100;
  },
  width: 135,
});

const getAvailableInvoiceColumn = (ctx: InMonthAdjustmentGridConfigContext): ColDef => {
  const { doesInvoiceMappingsMatchTotal, redirectFromInvoiceColumn } = ctx.qcaComponent;

  return {
    cellClass: (params) => ['text-right', ...getCellClassForGroups(params)],
    cellRenderer: AgIconLinkCellComponent,
    cellRendererParams: (params: ICellRendererParams) => {
      return {
        icons: [
          {
            icon: 'Check',
            iconClass: 'text-aux-green-dark',
            iconTooltipMessages: [MessagesConstants.INVOICE.TOTAL_MAPPED_TO_ACTIVITIES_THIS_PERIOD],
            showIcon: doesInvoiceMappingsMatchTotal(params),
          },
        ],
        rightAlignment: true,
      };
    },
    colId: 'invoices_available',
    field: 'invoices_available',
    filter: 'agNumberColumnFilter',
    headerClass: defaultHeaderClass,
    headerComponent: AgHeaderLinkComponent,
    headerComponentParams: {
      redirectFunction: redirectFromInvoiceColumn,
    },
    headerName: 'Available ($)',
    headerTooltip: 'Available ($)',
    hide: !ctx.flags.showAvailableInvoice,
    minWidth: defaultMinWidth,
    valueFormatter: currencyFormatter,
    width: defaultWidthForMoneyColumns,
  };
};

const getMonthlyExpenseColumn = (): ColDef => ({
  cellClass: (params) => ['text-right', ...getCellClassForGroups(params)],
  colId: 'invoices_amount',
  field: 'invoices_amount',
  filter: 'agNumberColumnFilter',
  headerClass: defaultHeaderClass,
  headerName: 'Monthly Expense ($)',
  headerTooltip: 'Monthly Expense ($)',
  minWidth: defaultMinWidth,
  valueFormatter: currencyFormatter,
  width: defaultWidthForMoneyColumns,
});
